/*
 * Mi Felpa — scripts globales de la tienda (los carga layout/theme.liquid en todas las
 * páginas):
 *   1. Guardián del carrito para «Estampado en ambos lados».
 *   2. Visitante de afuera de Chile: aviso, colecciones vacías y precio aproximado en su moneda.
 *
 * Hasta 2026-10-01 este archivo tenía
 * las «mejoras» del personalizador (presets de tamaño, reiniciar diseño, resumen, avisos),
 * subidas directo al tema sin pasar por el repo; ahora viven en mifelpa-polo-customizer.js.
 * Se reutiliza el mismo nombre para no tener que editar theme.liquid.
 *
 * Qué hace: la polera con «Ambos lados» y su adicional comparten la propiedad oculta
 * `_Diseño`. Cada vez que cambia el carrito, el adicional se iguala a la cantidad de su
 * polera, y si la polera se quita, el adicional también. En el carrito el adicional no
 * tiene botones de cantidad ni de quitar: va con su polera.
 */
(() => {
  'use strict';

  const ADDON_HANDLE = 'estampado-ambos-lados';
  const LINK_KEY = '_Diseño';
  const SOURCE = 'mifelpa-cart-guard';
  const root = () => window.Shopify?.routes?.root || '/';

  const injectStyle = () => {
    if (document.getElementById('mifelpa-cart-guard-style')) return;
    const style = document.createElement('style');
    style.id = 'mifelpa-cart-guard-style';
    style.textContent = `
      .cart-items__table-row:has(a[href*="/products/${ADDON_HANDLE}"]) .cart-items__quantity-controls {
        display: none;
      }
      .cart-items__table-row:has(a[href*="/products/${ADDON_HANDLE}"]) .cart-items__quantity::after {
        content: 'Va con tu polera';
        font-size: 0.85em;
        opacity: 0.7;
      }
    `;
    document.head.appendChild(style);
  };

  let running = false;
  let pending = false;

  const sync = async () => {
    if (running) {
      pending = true;
      return;
    }
    running = true;
    try {
      const cart = await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const items = cart.items || [];
      if (!items.some((item) => item.handle === ADDON_HANDLE)) return;

      const wanted = new Map();
      items.forEach((item) => {
        const id = item.properties?.[LINK_KEY];
        if (!id || item.handle === ADDON_HANDLE) return;
        wanted.set(id, (wanted.get(id) || 0) + item.quantity);
      });

      const updates = {};
      items.forEach((item) => {
        if (item.handle !== ADDON_HANDLE) return;
        const id = item.properties?.[LINK_KEY];
        if (!id) return;
        const quantity = wanted.get(id) || 0;
        if (quantity !== item.quantity) updates[item.key] = quantity;
      });
      if (!Object.keys(updates).length) return;

      const sectionIds = [...document.querySelectorAll('cart-items-component')]
        .map((element) => element.dataset.sectionId)
        .filter(Boolean);
      const body = { updates };
      if (sectionIds.length) body.sections = sectionIds.join(',');

      const result = await fetch(`${root()}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => r.json());

      document.dispatchEvent(
        new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: result,
            sourceId: SOURCE,
            data: { source: SOURCE, itemCount: result.item_count, sections: result.sections },
          },
        })
      );
    } catch (error) {
      console.error('MiFelpa: no se pudo ordenar el carrito', error);
    } finally {
      running = false;
      if (pending) {
        pending = false;
        sync();
      }
    }
  };

  injectStyle();
  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === SOURCE) return;
    // El carrito del tema termina de guardar el cambio antes de este evento; se da un
    // respiro para leer el estado ya guardado.
    setTimeout(sync, 250);
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', sync);
  } else {
    sync();
  }
})();

/*
 * 2. Visitante de afuera de Chile (mercados Norteamérica, Unión Europea y «Otros países»).
 *
 * Mi Felpa cobra con Mercado Pago, que en Chile solo acepta pesos chilenos, y Shopify solo
 * deja cobrar en otra moneda con Shopify Payments (no existe para tiendas de Chile). Por eso
 * el cobro es siempre en CLP y aquí solo se MUESTRA el monto aproximado en la moneda del
 * visitante, con el tipo de cambio del día (open.er-api.com, se guarda 12 h en el navegador).
 *
 * Además, los productos de Chile no se venden afuera (solo la colección internacional, de
 * Printful): para el visitante de afuera, las colecciones del menú salen vacías. Se le avisa
 * arriba y, en una colección vacía, se lo lleva a la internacional.
 *
 * Si se cambian los países de los mercados en el admin, actualizar INTERNATIONAL_COUNTRIES.
 */
(() => {
  'use strict';

  const INTERNATIONAL_COUNTRIES = new Set([
    // Norteamérica
    'US', 'CA', 'MX',
    // Unión Europea
    'AT', 'BE', 'BG', 'CY', 'CZ', 'DE', 'DK', 'EE', 'ES', 'FI', 'FR', 'GR', 'HR', 'HU', 'IE',
    'IT', 'LT', 'LU', 'LV', 'MT', 'NL', 'PL', 'PT', 'RO', 'SE', 'SI', 'SK',
    // Otros países (Printful)
    'AD', 'AL', 'AU', 'BA', 'BR', 'CH', 'GB', 'IS', 'JP', 'LI', 'MC', 'MD', 'ME', 'MK', 'NO',
    'NZ', 'RS', 'SM', 'UA', 'VA',
  ]);
  const CURRENCY_BY_COUNTRY = {
    US: 'USD', CA: 'CAD', MX: 'MXN', GB: 'GBP', CH: 'CHF', LI: 'CHF', AU: 'AUD', NZ: 'NZD',
    JP: 'JPY', NO: 'NOK', IS: 'ISK', BR: 'BRL', CZ: 'CZK', DK: 'DKK', PL: 'PLN', SE: 'SEK',
    HU: 'HUF', RO: 'RON', AL: 'ALL', BA: 'BAM', MK: 'MKD', MD: 'MDL', RS: 'RSD', UA: 'UAH',
  };
  const RATES_URL = 'https://open.er-api.com/v6/latest/CLP';
  const RATES_KEY = 'mf-rates-clp';
  const RATES_TTL_MS = 12 * 60 * 60 * 1000;
  const INTERNACIONAL_URL = '/collections/internacional';
  const PRICE_SELECTOR =
    '.price, .cart-items__price text-component, .cart-totals__total-value, .mifelpa-customizer__price-extra';

  const country = window.Shopify?.country;
  if (!country || !INTERNATIONAL_COUNTRIES.has(country)) return;

  const currency = CURRENCY_BY_COUNTRY[country] || 'EUR';
  const countryName = (() => {
    try {
      return new Intl.DisplayNames(['es'], { type: 'region' }).of(country) || country;
    } catch (_) {
      return country;
    }
  })();

  const storage = {
    get(key) {
      try {
        return JSON.parse(window.localStorage.getItem(key) || 'null');
      } catch (_) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, JSON.stringify(value));
      } catch (_) {
        // Sin almacenamiento (modo privado): se vuelve a pedir la tasa en la próxima página.
      }
    },
  };

  const injectStyle = () => {
    if (document.getElementById('mf-intl-style')) return;
    const style = document.createElement('style');
    style.id = 'mf-intl-style';
    style.textContent = `
      .mf-intl-notice {
        margin: 0 auto;
        padding: 0.75rem 1rem;
        background: #fff7e6;
        border-bottom: 1px solid #f0d9a8;
        color: #4a3b12;
        font-size: 0.9rem;
        line-height: 1.45;
        text-align: center;
      }
      .mf-intl-notice a, .mf-intl-empty a {
        color: inherit;
        font-weight: 700;
        text-decoration: underline;
      }
      .mf-intl-empty {
        max-width: 36rem;
        margin: 2rem auto;
        padding: 1.25rem 1.5rem;
        border: 1px solid rgba(0, 0, 0, 0.12);
        border-radius: 12px;
        text-align: center;
        line-height: 1.5;
      }
      .mf-intl-empty .mf-intl-cta {
        display: inline-block;
        margin-top: 0.75rem;
        padding: 0.65rem 1.1rem;
        border-radius: 999px;
        background: #111;
        color: #fff;
        text-decoration: none;
        font-weight: 600;
      }
      .mf-approx {
        display: block;
        font-size: 0.8em;
        opacity: 0.7;
        font-weight: 400;
      }
    `;
    document.head.appendChild(style);
  };

  // Aviso arriba de cada página.
  const renderNotice = () => {
    const main = document.querySelector('main');
    if (!main || document.querySelector('.mf-intl-notice')) return;
    const notice = document.createElement('div');
    notice.className = 'mf-intl-notice';
    notice.innerHTML =
      `Envíos a <b>${countryName}</b>: <a href="${INTERNACIONAL_URL}">colección internacional</a>. ` +
      `Se cobra en pesos chilenos (CLP); el monto en ${currency} es aproximado.`;
    main.insertAdjacentElement('beforebegin', notice);
  };

  // Colecciones del menú (productos de Chile): vacías para afuera → a la internacional.
  const renderEmptyCollection = () => {
    if (!/^\/collections\//.test(window.location.pathname)) return;
    if (window.location.pathname.startsWith(INTERNACIONAL_URL)) return;
    const main = document.querySelector('main');
    if (!main || main.querySelector('.mf-intl-empty')) return;
    const hasProducts = main.querySelector('product-card, .product-card, a[href*="/products/"]');
    if (hasProducts) return;
    const box = document.createElement('div');
    box.className = 'mf-intl-empty';
    box.innerHTML =
      `Estos productos se venden solo en Chile.<br>Para envíos a <b>${countryName}</b>, mira nuestra colección internacional.` +
      `<br><a class="mf-intl-cta" href="${INTERNACIONAL_URL}">Ver colección internacional</a>`;
    main.prepend(box);
  };

  const loadRates = async () => {
    const cached = storage.get(RATES_KEY);
    if (cached?.rates && Date.now() - cached.savedAt < RATES_TTL_MS) return cached.rates;
    try {
      const data = await fetch(RATES_URL).then((r) => r.json());
      if (data?.result !== 'success' || !data.rates) return cached?.rates || null;
      storage.set(RATES_KEY, { rates: data.rates, savedAt: Date.now() });
      return data.rates;
    } catch (_) {
      return cached?.rates || null;
    }
  };

  // «$30.000» / «$30.000 CLP» / «Con texto en ambos lados: $19.990» → 30000 (CLP no tiene decimales).
  const parseClp = (text) => {
    const match = String(text || '').match(/\$\s?([\d.,]+)/);
    if (!match) return null;
    const amount = Number(match[1].replace(/[.,]/g, ''));
    return Number.isFinite(amount) && amount > 0 ? amount : null;
  };

  const formatApprox = (value) =>
    `≈ ${new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency,
      maximumFractionDigits: value >= 100 ? 0 : 2,
      minimumFractionDigits: value >= 100 ? 0 : 2,
    }).format(value)}`;

  let rate = null;
  let scheduled = false;

  const renderApprox = () => {
    scheduled = false;
    if (!rate) return;
    document.querySelectorAll(PRICE_SELECTOR).forEach((element) => {
      if (element.closest('.mf-approx') || !element.getClientRects().length) return;
      const amount = parseClp(element.textContent);
      let tag = element.nextElementSibling?.classList?.contains('mf-approx') ? element.nextElementSibling : null;
      if (!amount) {
        tag?.remove();
        return;
      }
      const label = formatApprox(amount * rate);
      if (!tag) {
        tag = document.createElement('span');
        tag.className = 'mf-approx';
        element.insertAdjacentElement('afterend', tag);
      }
      if (tag.textContent !== label) tag.textContent = label;
    });
  };

  const schedule = () => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      renderApprox();
      renderEmptyCollection();
    });
  };

  const start = async () => {
    injectStyle();
    renderNotice();
    renderEmptyCollection();
    const rates = await loadRates();
    rate = rates?.[currency] || null;
    renderApprox();
    // Precios que el tema vuelve a dibujar (variante, carrito, filtros).
    new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true, characterData: true });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
